import React from 'react'
import uniqBy from 'lodash/uniqBy'
import { connect } from 'react-redux'
import { compose, lifecycle, withHandlers } from 'recompose'
import Shortcuts from '../../../../containers/Shortcuts'
import {
  duplicateBlocks,
  group,
  queueBlocks,
  removeBlocks,
  sendToBack,
  sendToFront,
  setBlockLayout,
  setBlockProperties,
  ungroup
} from '../../../../store/actions/blocks'
import { setTextEditMode } from '../../../../store/actions/boards'
import {
  selectBlocks,
  copyBlocks
} from '../../../../store/actions/selectedBlocks'
import { currentBoardColorSelector } from '../../../../store/selectors/selectors'
import { Popper } from 'react-popper'

export default BlockOptions => {
  const BlockOptionsWrapped = lifecycle({
    componentWillReceiveProps(newProps) {
      // check if the layout has changed and tell popper to update
      const check = side => newProps.layout[side] !== this.props.layout[side]
      if (check('top') || check('left') || check('right') || check('bottom')) {
        this.props.scheduleUpdate()
        return
      }

      // when popper first figures out placement, tell it to think harder
      // menus on the left will be incorrectly placed otherwise
      // https://github.com/FezVrasta/react-popper/issues/88
      if (!this.props.placement && newProps.placement === 'left-start') {
        this.props.scheduleUpdate()
      }
    }
  })(BlockOptions)

  return props => (
    <Popper
      placement="right-start"
      modifiers={{
        preventOverflow: {
          padding: { top: 40, bottom: 10, left: 10, right: 10 }
        },
        flip: { behavior: ['right', 'left', 'bottom-end'] }
      }}
    >
      {({ ref, layout, ...popperProps }) => (
        <BlockOptionsWrapped innerRef={ref} {...popperProps} {...props} />
      )}
    </Popper>
  )
}

const nudgeBlocks = (blocks, direction, distance) => {
  return setBlockLayout(
    blocks.map(block => {
      switch (direction) {
        case 'up':
          return { block, layout: { top: block.top - distance } }
        case 'down':
          return { block, layout: { top: block.top + distance } }
        case 'left':
          return { block, layout: { column: block.column - distance } }
        case 'right':
          return { block, layout: { column: block.column + distance } }
        default:
          return {}
      }
    })
  )
}

export const BlockEditOptionsContainer = compose(
  connect(
    ({ board: { textEditMode } }) => ({ textEditMode }),
    (dispatch, { blocks }) => ({
      removeBlocks: () => dispatch(removeBlocks(blocks)),
      sendToBack: () => dispatch(sendToBack(blocks)),
      sendToFront: () => dispatch(sendToFront(blocks)),
      duplicateBlocks: () => dispatch(duplicateBlocks(blocks)),
      copyBlocks: () => dispatch(copyBlocks(blocks)),
      queueBlocks: () => dispatch(queueBlocks(blocks)),
      selectBlocks: blocks => dispatch(selectBlocks(blocks)),
      nudgeBlocks: (direction, distance) =>
        dispatch(nudgeBlocks(blocks, direction, distance)),
      bigger: () =>
        dispatch(
          setBlockLayout(
            blocks.map(block => ({ block, layout: { width: block.width + 1 } }))
          )
        ),
      smaller: () =>
        dispatch(
          setBlockLayout(
            blocks.map(block => ({ block, layout: { width: block.width - 1 } }))
          )
        )
    })
  ),
  withHandlers({
    handleCopy: ({ copyBlocks, textEditMode }) => event => {
      if (textEditMode) return // not while editing text - just want normal copy
      // set clipboard data to none so we can use the block data from store
      // this.props.group.blocks.map(block => block.board block.id).join('\r\n')
      copyBlocks()
      event.clipboardData.setData('text/plain', '')
      event.clipboardData.setData('text/html', '')
      event.clipboardData.setData('text/uri-list', '')
      event.preventDefault()

      // const images = []
      // const text = []
      // const urls = []
      // this.props.group.blocks.forEach(block => {
      //   urls.push(window.location.origin + ...)
      // })
      // event.clipboardData.setData(
      //   'text/html',
      //   '<meta http-equiv="Content-Type" content="text/html;charset=UTF-8">' +
      //     this.props.group.blocks
      //       .map(
      //         block =>
      //           `<img src="${
      //             block.image.src
      //           }" alt="${block.name}" width="${block.width *
      //             this.props.colWidth}" height="${block.width * block.aspect * this.props.colWidth}">`
      //       )
      //       .join()
      // )
      // event.clipboardData.setData('text/uri-list', '#wiip.co') join /r/n
    }
  }),
  lifecycle({
    componentDidMount() {
      document.addEventListener('copy', this.props.handleCopy, false)
    },
    componentWillUnmount() {
      document.removeEventListener('copy', this.props.handleCopy)
    }
  }),
  Shortcuts('SELECTABLE', props => (action, event) => {
    if (props.textEditMode) return

    switch (action) {
      case 'DELETE':
        props.removeBlocks()
        break
      case 'SEND_BACK':
      case 'SEND_TO_BACK':
        props.sendToBack()
        event.preventDefault()
        break
      case 'SEND_FORWARD':
      case 'SEND_TO_FRONT':
        props.sendToFront()
        event.preventDefault()
        break
      // case 'MOVE_TO_QUEUE':
      //   props.queueBlocks()
      //   break
      // case 'COPY_TO_QUEUE':
      //   props.duplicateBlocks()
      //   break
      case 'MOVE_LEFT':
        props.nudgeBlocks('left', 0.1)
        break
      case 'MOVE_RIGHT':
        props.nudgeBlocks('right', 0.1)
        break
      case 'MOVE_UP':
        props.nudgeBlocks('up', 0.1)
        event.preventDefault()
        break
      case 'MOVE_DOWN':
        props.nudgeBlocks('down', 0.1)
        event.preventDefault()
        break
      case 'MOVE_LEFT_1':
        props.nudgeBlocks('left', 1)
        break
      case 'MOVE_RIGHT_1':
        props.nudgeBlocks('right', 1)
        break
      case 'MOVE_UP_1':
        props.nudgeBlocks('up', 1)
        event.preventDefault()
        break
      case 'MOVE_DOWN_1':
        props.nudgeBlocks('down', 1)
        event.preventDefault()
        break
      // case 'MAKE_BIGGER':
      //   props.bigger()
      //   break
      // case 'MAKE_SMALLER':
      //   props.smaller()
      //   break
      case 'DESELECT':
        props.selectBlocks([])
        event.preventDefault()
        break
      default:
    }
  })
)

export const BlockGroupingOptionsContainer = compose(
  connect(
    (state, { blocks }) => ({
      count: blocks.length,
      // get all blocks in unique groups
      uniqueGroups: blocks.length && uniqBy(blocks, block => block.group)
    }),
    (dispatch, { blocks }) => ({
      group: () => dispatch(group(blocks)),
      ungroup: () => dispatch(ungroup(blocks))
    }),
    (stateProps, dispatchProps, ownProps) => ({
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      // there is only one unique group and it exists
      grouped:
        stateProps.uniqueGroups.length === 1 &&
        !!stateProps.uniqueGroups[0].group
    })
  ),
  Shortcuts('GROUP', props => (action, event) => {
    if (action === 'GROUP') {
      if (props.grouped) {
        props.ungroup()
      } else {
        props.group()
      }
    }
  })
)

export const TextBlockOptionsContainer = connect(
  (state, { block }) => ({
    textEditMode: block && state.board.textEditMode === block.id,
    boardColor: currentBoardColorSelector(state)
  }),
  (dispatch, { block }) => ({
    setBlockProperties: update => dispatch(setBlockProperties(block, update)),
    setTextEditMode: id => dispatch(setTextEditMode(id)),
    selectBlocks: blocks => dispatch(selectBlocks(blocks))
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    toggleTextEdit: () => {
      dispatchProps.setTextEditMode(
        stateProps.textEditMode ? null : ownProps.block.id
      )
      dispatchProps.selectBlocks([ownProps.block.id])
    }
  })
)
